import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Text, Button, Checkbox, Link } from "@chakra-ui/react";
import { FaCcVisa, FaUniversity, FaDonate } from "react-icons/fa";
import { Link as ReactRouterLink } from "react-router-dom";
import { T_C_ROUTE } from "src/routePaths";
import { useSelector, RootState } from "src/store";

type props = {
  quotation: Quotation;
  selectMethod: (qId: number, name: string) => void;
  setHeighPrices: React.Dispatch<React.SetStateAction<boolean>>;
  setFileName: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSubscriptionData: React.Dispatch<
    React.SetStateAction<
      | {
          quotationId?: number;
          paymentMethod?: string;
          proveUrl?: string;
          nationalityProofUrl?: string;
          gateway?: 'tap' | 'myfatoorah';
        }
      | undefined
    >
  >;
  subscribeHeighPrice: (subData: {}) => void;
  setChangeQuotationSectionVisibility: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  handleOpening: (quotation: Quotation) => void;
  processing: boolean;
  selectPaymentMethods:
    | []
    | ["card"]
    | ["purchase"]
    | ["bank"]
    | ["card", "purchase"]
    | ["card", "bank"]
    | ["purchase", "card"]
    | ["purchase", "bank"]
    | ["bank", "card"]
    | ["bank", "purchase"]
    | ["card", "purchase", "bank"]
    | ["card", "bank", "purchase"]
    | ["purchase", "card", "bank"]
    | ["purchase", "bank", "card"]
    | ["bank", "card", "purchase"]
    | ["bank", "purchase", "card"];
  isFreeTrial?: boolean;
  onFreeTrialSubscribe?: () => void;
};

type Quotation = {
  id?: number;
  duration?: number;
  price?: number;
  currency?: "USD";
  region?: string;
  selected?: string;
};

const currencyMap = {
  USD: "$",
};

const paymentMethods = [
  { name: "card", Icon: FaCcVisa, color: "#6c5fe3" },
  { name: "purchase", Icon: FaDonate, color: "#0abf25" },
  { name: "bank", Icon: FaUniversity, color: "#f5b464" },
];

const SubscriptionCard = ({
  quotation,
  processing,
  selectMethod,
  setHeighPrices,
  setFileName,
  setSubscriptionData,
  subscribeHeighPrice,
  setChangeQuotationSectionVisibility,
  handleOpening,
  selectPaymentMethods = [],
  isFreeTrial = false,
  onFreeTrialSubscribe,
}: props) => {
  const [agreeOnTermsAndConditions, setAgreeOnTermsAndConditions] =
    useState<boolean>(false);

    const { locale } = useIntl();


  const user = useSelector((state: RootState) => state.auth.user);

  // only show the free trial option for users with no previous subscriptions (new users)
  // Check for active, expired, approved, or cancelled subscriptions (exclude only pending)
  const hasPreviousSubscription = user?.subscriptions?.some(
    (sub) => sub.status === "active" || sub.status === "expired" || sub.status === "approved" || sub.status === "cancelled"
  );
  const showFreeTrialOption = isFreeTrial && !hasPreviousSubscription;

  // Filter payment methods based on selectPaymentMethods prop
  const filteredPaymentMethods =
    selectPaymentMethods.length > 0
      ? paymentMethods.filter(({ name }) =>
          (selectPaymentMethods as string[]).includes(name)
        )
      : paymentMethods;

  if (isFreeTrial && !showFreeTrialOption) return <></>;

  return (
    <Box
      key={quotation.id}
      display="flex"
      flexDir="column"
      alignItems="center"
      padding="30px 0"
      borderRadius="15px"
      backgroundColor="white"
      boxShadow="0 2px 10px 2px silver"
      margin={{
        base: "30px 0 !important",
        lg: "30px !important",
      }}
      width={{ base: "300px", md: "350px" }}
      position="relative"
      overflow="hidden"
    >
      <Box padding="0 30px">
        <Text fontSize="36px" fontWeight="bold" color="brand.900">
          {isFreeTrial ? (
            <FormattedMessage id="subscription.freeTrial.title" />
          ) : (
            <>
              <Box
                as="span"
                fontFamily="'Roboto', 'Arial', sans-serif"
                sx={{
                  fontVariantNumeric: 'lining-nums',
                  fontFeatureSettings: '"lnum"',
                  direction: 'ltr',
                  unicodeBidi: 'embed',
                }}
                lang="en"
              >
                {quotation.duration}
              </Box>
              {' '}
              <FormattedMessage id="year" />
            </>
          )}
        </Text>
      </Box>

      {!isFreeTrial ? (
        <Box
          backgroundColor="brand.900"
          padding="30px 0"
          width="100%"
          marginTop="30px"
          maxHeight="100px"
        >
          <Text
            textAlign="center"
            color="white"
            fontSize="28px"
            fontWeight="bold"
          >
            {quotation.price?.toFixed(2)}
            <sup>{currencyMap[quotation.currency!]}</sup>
          </Text>
        </Box>
      ) : (
        <>
          <Box
            backgroundColor="brand.900"
            width="100%"
            marginTop="30px"
            maxHeight="100px"
            height="100px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
          >
            <Text
              textAlign="center"
              color="white"
              fontSize="28px"
              fontWeight="bold"
            >
              <FormattedMessage id={"subscription.freeTrial.subtitle" || ""} />
            </Text>
          </Box>
        </>
      )}
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="center"
        marginTop="30px"
      >
        {!isFreeTrial ? (
          filteredPaymentMethods.map(({ name, Icon, color }) => (
            <Box
              title={name}
              key={name}
              margin="15px"
              width="80px"
              paddingTop="10px"
              display="flex"
              flexDir="column"
              alignItems="center"
              borderRadius="10px"
              background={name === quotation.selected ? "#b2caf7" : "white"}
              onClick={() => {
                selectMethod(quotation.id!, name);
                // Just select the payment method, don't show popup yet
              }}
              cursor="pointer"
              transition="150ms ease-out"
              _hover={{ background: "#b2caf7" }}
            >
              <Icon size={46} height="70px" width="70px" color={color} />
              <Text my={3} fontWeight="600">
                <FormattedMessage id={name || ""} />
              </Text>
            </Box>
          ))
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="134px"
          >
            <Text
              color="brand.900"
              textStyle="smallBold"
              fontWeight="800"
              fontSize="18px"
              fontFamily="Calibri, sans-serif"
              padding={locale === "ar" ? "0 5px" : "0"}
              textAlign={locale === "ar" ? "center" : "left"}
              direction={locale === "ar" ? "rtl" : "ltr"}
              whiteSpace="normal"
              wordBreak="break-word"
            >
              <FormattedMessage id={"subscription.freeTrial.content" || ""} />
            </Text>
          </Box>
        )}
      </Box>
      <Box marginTop="30px">
        {processing && (
          <Box margin="30px 0">
            <Text color="grey">
              <FormattedMessage id="wait" />
            </Text>
          </Box>
        )}
        {/* setFileName('proveUrl') */}
        <Button
          backgroundColor="brand.900"
          color="white"
          height="50px"
          _hover={{ filter: "brightness(120%)" }}
          onClick={() => {
            if (isFreeTrial) {
              onFreeTrialSubscribe?.();
              return;
            }

            const isValidPrice =
              quotation.price === 245 ||
              quotation.price === 375 ||
              quotation.price === 0;

            if (isValidPrice) setHeighPrices(true);

            if (isValidPrice && quotation.selected === "bank") {
              setFileName("proveUrl");
              handleOpening(quotation);
              return;
            }
            
            // For card payments, show gateway selection modal
            if (isValidPrice && quotation.selected === "card") {
              handleOpening(quotation);
              return;
            }
            
            // For other payment methods, proceed directly
            if (isValidPrice && quotation.selected !== "bank" && quotation.selected !== "card") {
              let subData = {
                quotationId: quotation.id,
                paymentMethod: quotation.selected,
              };

              setSubscriptionData({
                quotationId: quotation.id,
                paymentMethod: quotation.selected,
              });

              subscribeHeighPrice(subData);
              setChangeQuotationSectionVisibility(false);
              return;
            }
            
            // For regular prices (not high prices), show modal
            handleOpening(quotation);
          }}
          disabled={
            (!isFreeTrial && !quotation.selected) ||
            processing ||
            !agreeOnTermsAndConditions
          }
        >
          <FormattedMessage id="subscribe" />
        </Button>
      </Box>
      <Checkbox
        type="checkbox"
        name="complete"
        isChecked={agreeOnTermsAndConditions}
        onChange={() => setAgreeOnTermsAndConditions((prev) => !prev)}
        marginTop="20px"
      >
        <FormattedMessage id="agreeTerms" />{" "}
        <Link
          as={ReactRouterLink}
          to={T_C_ROUTE}
          color="brand.900"
          textStyle="smallBold"
        >
          <FormattedMessage id="t&c" />
        </Link>
      </Checkbox>
    </Box>
  );
};

export default SubscriptionCard;
